/**
 * Roles master-detail: the role catalogue on the left, the selected role's
 * members + grants on the right.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { StaleDataNotice } from '@/components/clinical';
import { EmptyState, ErrorState, LoadingRows, Panel } from './OrgUi';
import { RoleDetail } from './RoleDetail';
import { RoleFormDialog } from './OrgDialogs';
import { useRoles } from './useOrg';
import type { Role } from '@/api/endpoints/org';

export function RolesView() {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching, error, refetch } = useRoles();
  const roles = data;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const byId = useMemo(() => {
    const m = new Map<string, Role>();
    for (const r of roles ?? []) m.set(r.id, r);
    return m;
  }, [roles]);

  useEffect(() => {
    if (selectedId && roles && !byId.has(selectedId)) setSelectedId(null);
  }, [roles, byId, selectedId]);

  const selected = selectedId ? byId.get(selectedId) ?? null : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(260px,320px)_1fr]">
      <Panel className="flex max-h-[70vh] min-h-[420px] flex-col p-3">
        <div className="mb-2 flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-neutral-900">{t('org.rolesView.roles', 'Roles')}</h2>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={() => setCreating(true)}>
            {t('org.rolesView.new', '+ New')}
          </Button>
        </div>
        <div className="-mx-1 flex-1 overflow-y-auto px-1">
          {/* A background refetch failing (or in flight) with a cached role
              list still showing must stay visible — the list-below render
              path has no other signal that it might be stale. */}
          {roles && <StaleDataNotice isFetching={isFetching} isError={isError} className="mb-2 px-1" />}
          {isError && !roles ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : isLoading ? (
            <LoadingRows rows={5} />
          ) : (roles?.length ?? 0) === 0 ? (
            <EmptyState
              title={t('org.rolesView.emptyTitle', 'No roles yet')}
              hint={t(
                'org.rolesView.emptyHint',
                'A role groups people by function — ICU Nurses, Ward Doctors — so you assign access once, to the role.',
              )}
              action={
                <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
                  {t('org.rolesView.createRole', 'Create a role')}
                </Button>
              }
            />
          ) : (
            <ul className="space-y-0.5">
              {roles?.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelectedId(r.id)}
                    className={cn(
                      'w-full rounded-lg px-3 py-2 text-left transition-colors',
                      selectedId === r.id ? 'bg-primary/10' : 'hover:bg-neutral-50',
                    )}
                  >
                    <div className={cn('truncate text-sm', selectedId === r.id ? 'font-medium text-primary' : 'text-neutral-800')}>
                      {r.name}
                    </div>
                    {r.description && <div className="truncate text-xs text-neutral-400">{r.description}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      <Panel className="min-h-[420px] p-6">
        {selected ? (
          <RoleDetail key={selected.id} role={selected} onDeleted={() => setSelectedId(null)} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <EmptyState
              title={
                (roles?.length ?? 0) === 0
                  ? t('org.rolesView.defineRoles', 'Define your roles')
                  : t('org.rolesView.selectRole', 'Select a role')
              }
              hint={
                (roles?.length ?? 0) === 0
                  ? t('org.rolesView.createFirstHint', 'Create the first role on the left.')
                  : t('org.rolesView.selectRoleHint', 'Pick a role to manage its members and what it can reach.')
              }
            />
          </div>
        )}
      </Panel>

      <RoleFormDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
